import type { components } from '~/generated/api/schema'
import type {
  CapabilitiesView,
  CapabilityModuleView,
  PrincipalIdentityRequirementView,
  PrincipalView
} from '~/shared-kernel/domain/auth'

type TokenPrincipalDto = components['schemas']['AuthenticatedPrincipalTokenViewDto']
type PrincipalDto = components['schemas']['AuthenticatedPrincipalResponseDto']
type CapabilitiesDto = components['schemas']['CapabilitiesResponseDto']
type ModuleDto = components['schemas']['CapabilitiesModuleDto']

export function mapTokenPrincipal(dto: TokenPrincipalDto | PrincipalDto): PrincipalView {
  // Token-view DTOs carry no access methods; only /auth/me resolves them.
  const accessMethods = 'accessMethods' in dto ? dto.accessMethods : undefined
  const principal: PrincipalView = {
    id: dto.id,
    principalType: dto.principalType,
    loginIdentifier: dto.loginIdentifier,
    displayName: dto.displayName,
    status: dto.status,
    ...(accessMethods
      ? { accessMethods: { password: accessMethods.password, google: accessMethods.google ? { email: accessMethods.google.email } : null } }
      : {})
  }

  if (!('tenant' in dto)) return principal

  return {
    ...principal,
    tenant: { ...dto.tenant },
    roles: dto.roles.map(role => ({ ...role })),
    emailVerifiedAt: dto.emailVerifiedAt,
    thirdPartyId: dto.thirdPartyId,
    locale: dto.locale,
    identityRequirement: mapIdentityRequirement(dto.identityRequirement)
  }
}

function mapIdentityRequirement(value: PrincipalDto['identityRequirement']): PrincipalIdentityRequirementView | null {
  if (!value) return null
  return { code: value.code, actionPath: value.actionPath }
}

function mapModule(dto: ModuleDto): CapabilityModuleView {
  return {
    key: dto.key,
    name: dto.name,
    children: dto.children.map(mapModule)
  }
}

export function mapCapabilities(dto: CapabilitiesDto): CapabilitiesView {
  return {
    principalType: dto.principalType,
    modules: dto.modules.map(mapModule),
    permissions: dto.permissions.map(permission => ({
      module: permission.module,
      subject: permission.subject,
      action: permission.action,
      scope: permission.scope
    }))
  }
}
